import { createFileRoute } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { formatCents } from '@pirouette/shared';
import { Badge, type BadgeProps } from '@/components/ui/Badge';
import { Empty } from '@/components/ui/Empty';
import {
  usePaymentPlans,
  usePaymentPlanInstallments,
  useRefunds,
  type PaymentPlanRow,
  type PaymentPlanInstallmentRow,
  type RefundRow,
} from '@/data/payments';

// Payments scaffold screen (Phase 8).
//
// SCAFFOLD ONLY. Payment plans, installments, and refunds are modeled in the
// database, but no provider behaviour is active: ACH debit, refund execution,
// and installment charging stay off until this studio's Stripe is connected and
// tested. The screen LEADS with a prominent amber banner saying exactly that,
// then renders read-only tables over the (pre-launch, empty) tables. There are
// no actions here — the data layer exposes reads only.
//
// Built on the same layout primitives as the owner BI dashboard / staff console
// (Section / SimpleTable / Num) so it reads as one product. Those helpers are
// copied per-route in this codebase (not shared), matching admin.bi.index.tsx /
// admin.staff.index.tsx.
//
// The /admin parent route already gates on the `admin` role server-side, so this
// child route inherits that guard (matches every other admin.* route).
export const Route = createFileRoute('/admin/payments/')({
  component: PaymentsScaffold,
});

type BadgeTone = NonNullable<BadgeProps['tone']>;

// ---------------------------------------------------------------------------
// Status tones
// ---------------------------------------------------------------------------

// payment_plans.status in draft/active/completed/cancelled.
function planTone(status: string): BadgeTone {
  if (status === 'active') return 'info';
  if (status === 'completed') return 'success';
  if (status === 'cancelled') return 'danger';
  return 'neutral'; // draft / anything else
}

// payment_plan_installments.status in scheduled/charged/paid/failed/skipped.
function installmentTone(status: string): BadgeTone {
  if (status === 'paid') return 'success';
  if (status === 'charged') return 'info';
  if (status === 'failed') return 'danger';
  if (status === 'skipped') return 'neutral';
  return 'warn'; // scheduled / anything else pending
}

// refunds.status in requested/processing/completed/failed.
function refundTone(status: string): BadgeTone {
  if (status === 'completed') return 'success';
  if (status === 'processing') return 'info';
  if (status === 'failed') return 'danger';
  return 'warn'; // requested / anything else pending
}

// ---------------------------------------------------------------------------
// Layout primitives (mirror admin.bi.index.tsx / admin.staff.index.tsx)
// ---------------------------------------------------------------------------

function Section({
  id,
  title,
  description,
  action,
  children,
}: {
  id: string;
  title: string;
  description: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={`${id}-heading`} className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id={`${id}-heading`} className="text-[var(--text-xl)]">
            {title}
          </h2>
          <p className="mt-1 max-w-3xl text-[var(--color-ink-muted)] text-[var(--text-sm)]">
            {description}
          </p>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function SimpleTable({ columns, rows }: { columns: string[]; rows: ReactNode[][] }) {
  return (
    <div className="surface overflow-x-auto">
      <table className="min-w-full text-left text-[var(--text-sm)]">
        <thead>
          <tr className="border-b border-[var(--color-border)]">
            {columns.map((c) => (
              <th key={c} className="px-4 py-3 font-medium">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-[var(--color-border)] last:border-0">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-3 align-top">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Num({ value }: { value: number }) {
  return <span className="font-medium tabular-nums">{value}</span>;
}

// ---------------------------------------------------------------------------
// Scaffold banner — leads the screen. Amber/warn tone, visually distinct from
// the read-only tables below, so it is unmistakable that no provider behaviour
// is live.
// ---------------------------------------------------------------------------

function ScaffoldBanner() {
  return (
    <section
      aria-labelledby="scaffold-banner-heading"
      className="surface-elevated space-y-3 border-l-4 border-[var(--color-warn)] p-6"
    >
      <div className="flex flex-wrap items-center gap-3">
        <Badge tone="warn">Scaffold</Badge>
        <h2
          id="scaffold-banner-heading"
          className="tracking-[var(--tracking-tight)] text-[var(--text-lg)]"
        >
          Provider configuration required
        </h2>
      </div>
      <p className="max-w-3xl leading-[var(--leading-relaxed)] text-[var(--color-ink-muted)]">
        Payment plans, installments, and refunds are modeled here, but ACH debit, refund execution,
        and installment charging stay inactive until this studio&rsquo;s Stripe is connected and
        tested.
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Payment plans
// ---------------------------------------------------------------------------

function PlansSection({
  rows,
  loading,
  error,
}: {
  rows: PaymentPlanRow[];
  loading: boolean;
  error: boolean;
}) {
  return (
    <Section
      id="plans"
      title="Payment plans"
      description="Installment plans modeled per family. Read-only — plans are not created or charged until Stripe is connected and tested."
    >
      {loading ? (
        <p aria-live="polite">Loading…</p>
      ) : error ? (
        <Empty
          title="Payment plans unavailable"
          description="The payment plans could not be loaded for this session. Refresh to retry."
        />
      ) : rows.length === 0 ? (
        <Empty
          title="No payment plans yet"
          description="No payment plans have been modeled for this studio. Plans will appear here once the payments provider is configured and plans are created."
        />
      ) : (
        <SimpleTable
          columns={['Family', 'Total', 'Installments', 'Status']}
          rows={rows.map((p) => [
            <span className="font-mono text-[var(--color-ink-muted)] text-[var(--text-xs)]">
              {p.family_id.slice(0, 8)}
            </span>,
            <span className="tabular-nums">{formatCents(p.total_cents)}</span>,
            <Num value={p.installment_count} />,
            <Badge tone={planTone(p.status)}>{p.status}</Badge>,
          ])}
        />
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Installments
// ---------------------------------------------------------------------------

function formatDate(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function InstallmentsSection({
  rows,
  loading,
  error,
}: {
  rows: PaymentPlanInstallmentRow[];
  loading: boolean;
  error: boolean;
}) {
  return (
    <Section
      id="installments"
      title="Installments"
      description="Scheduled installment charges across all plans. Read-only — installments are not charged until Stripe is connected and tested."
    >
      {loading ? (
        <p aria-live="polite">Loading…</p>
      ) : error ? (
        <Empty
          title="Installments unavailable"
          description="The installment schedule could not be loaded for this session. Refresh to retry."
        />
      ) : rows.length === 0 ? (
        <Empty
          title="No installments yet"
          description="No installments have been scheduled. They will appear here once payment plans exist and the provider is configured."
        />
      ) : (
        <SimpleTable
          columns={['Due date', 'Amount', 'Status']}
          rows={rows.map((i) => [
            <span className="tabular-nums">{formatDate(i.due_date)}</span>,
            <span className="tabular-nums">{formatCents(i.amount_cents)}</span>,
            <Badge tone={installmentTone(i.status)}>{i.status}</Badge>,
          ])}
        />
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Refunds
// ---------------------------------------------------------------------------

function RefundsSection({
  rows,
  loading,
  error,
}: {
  rows: RefundRow[];
  loading: boolean;
  error: boolean;
}) {
  return (
    <Section
      id="refunds"
      title="Refunds"
      description="Refund requests against recorded payments. Read-only — refunds are not executed until Stripe is connected and tested."
    >
      {loading ? (
        <p aria-live="polite">Loading…</p>
      ) : error ? (
        <Empty
          title="Refunds unavailable"
          description="The refund list could not be loaded for this session. Refresh to retry."
        />
      ) : rows.length === 0 ? (
        <Empty
          title="No refunds yet"
          description="No refunds have been requested. Requests will appear here once the payments provider is configured."
        />
      ) : (
        <SimpleTable
          columns={['Amount', 'Reason', 'Status']}
          rows={rows.map((r) => [
            <span className="tabular-nums">{formatCents(r.amount_cents)}</span>,
            <span className="text-[var(--color-ink-muted)]">{r.reason ?? '—'}</span>,
            <Badge tone={refundTone(r.status)}>{r.status}</Badge>,
          ])}
        />
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

function PaymentsScaffold() {
  const plans = usePaymentPlans();
  const installments = usePaymentPlanInstallments();
  const refunds = useRefunds();

  return (
    <div className="space-y-10">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Billing
        </p>
        <h1 className="mt-2">Payments</h1>
      </header>

      <ScaffoldBanner />

      <PlansSection
        rows={plans.data ?? []}
        loading={plans.isLoading}
        error={Boolean(plans.error)}
      />
      <InstallmentsSection
        rows={installments.data ?? []}
        loading={installments.isLoading}
        error={Boolean(installments.error)}
      />
      <RefundsSection
        rows={refunds.data ?? []}
        loading={refunds.isLoading}
        error={Boolean(refunds.error)}
      />
    </div>
  );
}
